import { http } from "./http";
import { ResolvedReference, WorkspaceReference } from "./references.api";
export type PlanningPreview = {
  timezone: string;
  items: {
    candidateName: string;
    /** Identifiants de la leçon proposée : repris tels quels quand l'utilisateur corrige la carte. */
    candidateId?: string;
    instructorId?: string;
    vehicleId?: string | null;
    /** Absent quand l'école ne gère pas de parc de véhicules. */
    vehicleLabel: string | null;
    vehicleType: string;
    instructorName: string;
    startsAt: string;
    endsAt: string;
    previousInstructor?: string;
    lessonId?: string;
  }[];
  unresolved: {
    lessonId: string;
    candidateName: string;
    startsAt: string;
    reason: string;
  }[];
  warnings: string[];
  absence?: { instructorName: string; date: string; alreadyRecorded: boolean };
  /** Ce que le serveur a proposé lui-même, faute de précision dans la demande. */
  completed?: ("instructor" | "vehicle" | "duration")[];
  /** Créneaux à ouvrir à la réservation, et ceux écartés avec leur motif. */
  openSlots?: {
    instructorName: string;
    durationMinutes: number;
    allowedVehicleTypes: string[] | null;
    slots: { startsAt: string; endsAt: string }[];
    skipped: { startsAt: string; endsAt: string; reason: string }[];
  };
};
/** Carte de leçon préparée ou corrigée sans passer par l'assistant ; `null` : au serveur de proposer. */
export type AiLessonCard = {
  requestId: string;
  replacesProposalId?: string;
  candidateId: string;
  instructorId: string | null;
  vehicleId: string | null;
  date: string;
  time: string;
  durationMinutes: number | null;
};
export type AiProposal = {
  planId?: string | null;
  supersedesId?: string | null;
  supersededBy?: string | null;
  id: string;
  version: number;
  status: "pending" | "confirmed" | "cancelled";
  expiresAt: string;
  references: ResolvedReference[];
  result: { id: string; href: string } | null;
} & (
  | {
      delivery?: {
        items: {
          id: string;
          label: string;
          state: string;
          issue: string | null;
        }[];
        observedAt: string;
      } | null;
      action:
        | "rescheduleLesson"
        | "cancelLesson"
        | "pauseExamRequest"
        | "resumeExamRequest"
        | "requestDocuments"
        | "createInvoiceDraft";
      command: {
        title: string;
        description: string;
        references: WorkspaceReference[];
        preview: {
          changes: {
            label: string;
            before: string | null;
            after: string | null;
          }[];
          notices: string[];
        };
      };
    }
  | {
      action:
        | "assignTask"
        | "setTaskDueDate"
        | "updateTask"
        | "completeTask"
        | "reopenTask";
      command: {
        title: string;
        description: string;
        references: WorkspaceReference[];
        preview: {
          taskTitle: string;
          version: number;
          changes: {
            field: string;
            label: string;
            before: string | null;
            after: string | null;
          }[];
          notices: string[];
        };
      };
    }
  | {
      action:
        | "createCandidate"
        | "updateCandidate"
        | "changeCandidateCategory"
        | "changeCandidateStatus"
        | "requestCandidateMandate"
        | "inviteCandidate"
        | "requestCandidateEvaluation";
      delivery?: {
        state: string;
        label: string;
        issue: string | null;
        observedAt: string | null;
      } | null;
      command: {
        title: string;
        description: string;
        references: WorkspaceReference[];
        preview: {
          candidateName: string;
          version: number | null;
          changes: {
            field: string;
            label: string;
            before: string | null;
            after: string | null;
          }[];
          notices: string[];
        };
      };
    }
  | {
      action: "createTask";
      command: {
        title: string;
        description: string;
        priority: "normal" | "high" | "urgent";
        references: WorkspaceReference[];
      };
    }
  | {
      action: "createExamRequest";
      command: {
        title: string;
        description: string;
        references: WorkspaceReference[];
        operation: {
          candidates: { id: string; name: string; vehicleType: string }[];
          centers: { id: string; label: string; codeDepartement: string }[];
          permitGroup: string;
          dateStart: string;
          dateEnd: string;
          timeStart: string;
          timeEnd: string;
          timezone: string;
        };
      };
      delivery?: {
        items: {
          id: string;
          state: string;
          label: string;
          issue: string | null;
        }[];
        observedAt: string;
      } | null;
    }
  | {
      action: "sendCandidateMessage" | "sendContractForSignature";
      command: {
        title: string;
        description: string;
        references: WorkspaceReference[];
        operation: {
          candidateId: string;
          recipient: string;
          recipientName: string;
          subject: string;
          body: string;
          documentName?: string;
          documentHash?: string;
          sender?: { name: string; environment: string };
          signers?: { role: string; name: string; email: string }[];
          contractId?: string;
        };
      };
      delivery?: {
        state: string;
        label: string;
        reference: string | null;
        observedAt: string | null;
        issue: string | null;
        hasSignedDocument: boolean;
      } | null;
    }
  | {
      action: "scheduleLesson" | "reorganizeDay" | "openSlots";
      command: {
        title: string;
        description: string;
        references: WorkspaceReference[];
        preview: PlanningPreview;
      };
    }
);
export type AiHistory = { role: "user" | "assistant"; text: string };
export type AiAnswer = {
  requestId: string;
  answer: string;
  sources: ResolvedReference[];
  proposals: AiProposal[];
  plans?: AiPlan[];
  knowledge?: {
    citations: KnowledgeCitation[];
    citedIds: string[];
    grounded: boolean;
    hasMore: boolean;
    scope: string;
  };
  readouts?: {
    title: string;
    observedAt: string;
    lines: string[];
    notices: string[];
  }[];
  /** Pages du guide de Permio lues pour répondre (question d'utilisation). */
  guide?: { pages: AiGuidePage[] };
  observedAt: string;
  /** Discussion longue : nombre d'échanges anciens que l'assistant ne relisait plus pour cette réponse. */
  forgottenTurns?: number;
};
/**
 * Page du guide d'utilisation de Permio (son titre et l'écran qu'elle décrit) ou fiche du référentiel des
 * procédures (`kind: "procedure"`, ouverte dans la page Guide d'utilisation).
 */
export type AiGuidePage = { id: string; title: string; path: string; kind?: "procedure" };
export type KnowledgeCitation = {
  id: string;
  noteId: string;
  title: string;
  version: number;
  excerpt: string;
  startLine: number;
  endLine: number;
  href: string;
  publishedAt: string;
  sourceHash: string;
};
export type KnowledgeSource = {
  id: string;
  title: string;
  version: number;
  visibility: string;
  canPublish: boolean;
  enabled: boolean;
  publishedVersion: number | null;
  publishedAt: string | null;
  state: "excluded" | "stale" | "ready";
};
export type AiPlan = {
  id: string;
  title: string;
  status: "preparing" | "ready" | "cancelled";
  expiresAt: string;
  steps: {
    key: string;
    dependsOn: string[];
    state:
      | "pending"
      | "prepared"
      | "failed"
      | "blocked"
      | "confirmed"
      | "cancelled";
    issue: string | null;
    proposal: AiProposal | null;
    dependencySatisfied: boolean;
    waitingForResult?: boolean;
  }[];
};
export type AiProgress = {
  phase: "analysis" | "reading" | "preparing";
  completedTools: number;
};
export type AiMessage = {
  conversationId?: string;
  requestId: string;
  message: string;
  references: WorkspaceReference[];
  history: AiHistory[];
};
export type AiPlanDecision = {
  expectedProposals?: { key: string; proposalId: string }[];
  decision: "confirm" | "cancel";
  attemptId: string;
  selectedKeys: string[];
  acknowledgePlanningChecks: boolean;
  acknowledgeOperationReview: boolean;
};
export class AiStreamError extends Error {}
import { tenantEpoch } from "./tenant-context";
async function streamMessage(
  body: AiMessage,
  onProgress: (event: AiProgress) => void,
  signal: AbortSignal,
): Promise<AiAnswer> {
  const epoch = tenantEpoch();
  const { data } = await http.post<ReadableStream<Uint8Array>>(
    "/v1/ai/messages/stream",
    body,
    { adapter: "fetch", responseType: "stream", timeout: 240000, signal },
  );
  const reader = data.getReader(),
    decoder = new TextDecoder();
  let pending = "",
    bytes = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (signal.aborted || epoch !== tenantEpoch())
        throw new AiStreamError("Demande interrompue : le contexte a changé.");
      if (done) break;
      bytes += value.byteLength;
      if (bytes > 2 * 1024 * 1024)
        throw new AiStreamError("Réponse trop volumineuse.");
      pending += decoder.decode(value, { stream: true });
      let end: number;
      while ((end = pending.indexOf("\n")) !== -1) {
        const line = pending.slice(0, end);
        pending = pending.slice(end + 1);
        if (!line.trim()) continue;
        const frame = JSON.parse(line);
        if (frame.type === "error")
          throw new AiStreamError(
            typeof frame.message === "string"
              ? frame.message
              : "Demande interrompue.",
          );
        if (frame.type === "completed") return frame.answer as AiAnswer;
        if (
          frame.type === "progress" &&
          ["analysis", "reading", "preparing"].includes(frame.phase)
        )
          onProgress(frame);
      }
    }
    throw new AiStreamError(
      "La connexion a été interrompue. Les actions préparées restent disponibles.",
    );
  } finally {
    await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}
/** Initiative de Permio AI : une situation signalée sans qu'on la lui demande. */
export type AiInitiative = {
  id: string;
  kind: "reorganizeDay" | "examReadiness" | "receivableReminder" | "freedSlot";
  title: string;
  detail: string;
  /** Une carte à valider, une demande prête dans la saisie, un écran où agir, ou plusieurs cartes au choix. */
  opening: "card" | "prompt" | "link" | "choice";
  /** Cartes possibles (« Proposer à … ») : seul le rang choisi part à l'ouverture. */
  choices?: string[];
  createdAt: string;
  /** Carte que ce compte a déjà préparée et qui attend encore sa décision. */
  proposalId: string | null;
};
/** Bilan d'une nature d'initiative sur la période : ce que l'école a fait de ce que Permio a préparé. */
export type AiInitiativeUsage = {
  kind: AiInitiative["kind"];
  label: string;
  opening: AiInitiative["opening"];
  prepared: number;
  open: number;
  /** Carte validée telle quelle, ou demande reprise dans la saisie. */
  accepted: number;
  revised: number;
  dismissed: number;
  resolved: number;
  expired: number;
  stale: number;
  /** Traitées seul par Permio, dans le cadre fixé par l'école : ce ne sont pas des décisions. */
  delegated?: number;
  decided: number;
  /** En %, ou `null` tant que les décisions sont trop peu nombreuses. */
  acceptedAsIsRate: number | null;
  medianDecisionMinutes: number | null;
};
export type AiInitiativeUsageReport = {
  days: number;
  minDecisions: number;
  kinds: AiInitiativeUsage[];
};
/** Brouillon de réponse au dernier message d'un élève, préparé par Permio pour le bureau. */
export type AiStudentDraft =
  | { status: "none" }
  | { status: "preparing"; sequence: number }
  | {
      status: "ready";
      id: string;
      sequence: number;
      body: string;
      /** Report de leçon demandé par l'élève, vérifié par le planning ; la réponse l'annonce. */
      move?: AiStudentMove;
    };
/** Report vérifié : début actuel et début demandé de la leçon, fait ou en attente du bureau. */
export type AiStudentMove = {
  from: string;
  to: string;
  instructorName: string | null;
  /** Fuseau de l'école : les heures de la carte sont celles de la réponse. */
  timezone?: string;
  done: boolean;
};
/** Réponses automatiques de Permio aux élèves : réglage de l'école et bilan des 30 derniers jours. */
export type AiStudentReplies = {
  enabled: boolean;
  since: string | null;
  last30Days: { replied: number; escalated: number };
};
/** Réglage lu tel quel, sans se fier à la forme de la réponse. */
const studentRepliesOf = (data: Partial<AiStudentReplies> | undefined) => ({
  enabled: data?.enabled === true,
  since: typeof data?.since === "string" ? data.since : null,
  last30Days: {
    replied: Number(data?.last30Days?.replied) || 0,
    escalated: Number(data?.last30Days?.escalated) || 0,
  },
});
/** Une action que Permio peut faire seul : réglage, bilan qui l'ouvre et décompte des 30 derniers jours. */
export type AiDelegationAction = {
  kind: string;
  label: string;
  description: string;
  enabled: boolean;
  maxLevel: number;
  maxLevelLimit: number;
  since: string | null;
  eligibility: {
    eligible: boolean;
    decided: number;
    accepted: number;
    rate: number | null;
    /** Ce qui manque pour activer, en une phrase. */
    missing: string | null;
  };
  last30Days: { done: number; failed: number };
};
/** Ce que Permio a fait seul : relance envoyée, refusée ou incertaine. */
export type AiDelegatedAction = {
  id: string;
  kind: string;
  state: "running" | "done" | "failed" | "unknown";
  title: string;
  detail: string;
  at: string;
};
export type AiDelegations = {
  periodDays: number;
  minDecisions: number;
  minRate: number;
  hours: { from: string; until: string };
  actions: AiDelegationAction[];
  journal: AiDelegatedAction[];
};
const countOf = (value: unknown) => Math.max(0, Number(value) || 0);
const textOf = (value: unknown) => (typeof value === "string" ? value : "");
/** Délégation lue telle quelle, sans se fier à la forme de la réponse. */
const delegationsOf = (data: Partial<AiDelegations> | undefined) => ({
  periodDays: countOf(data?.periodDays) || 90,
  minDecisions: countOf(data?.minDecisions) || 10,
  minRate: countOf(data?.minRate) || 80,
  hours: {
    from: textOf(data?.hours?.from) || "09:00",
    until: textOf(data?.hours?.until) || "18:00",
  },
  actions: (Array.isArray(data?.actions) ? data.actions : []).map(
    (action): AiDelegationAction => ({
      kind: textOf(action?.kind),
      label: textOf(action?.label),
      description: textOf(action?.description),
      enabled: action?.enabled === true,
      maxLevel: countOf(action?.maxLevel) || 1,
      maxLevelLimit: countOf(action?.maxLevelLimit) || 1,
      since: typeof action?.since === "string" ? action.since : null,
      eligibility: {
        eligible: action?.eligibility?.eligible === true,
        decided: countOf(action?.eligibility?.decided),
        accepted: countOf(action?.eligibility?.accepted),
        rate:
          typeof action?.eligibility?.rate === "number"
            ? action.eligibility.rate
            : null,
        missing:
          typeof action?.eligibility?.missing === "string"
            ? action.eligibility.missing
            : null,
      },
      last30Days: {
        done: countOf(action?.last30Days?.done),
        failed: countOf(action?.last30Days?.failed),
      },
    }),
  ),
  journal: (Array.isArray(data?.journal) ? data.journal : []).filter(
    (item): item is AiDelegatedAction =>
      typeof item?.id === "string" &&
      typeof item?.title === "string" &&
      typeof item?.at === "string",
  ),
});
export type AiInitiativeOpening =
  | { opening: "card"; proposal: AiProposal }
  | {
      opening: "prompt";
      text: string;
      references: (WorkspaceReference & { token: string })[];
    }
  /** Chemin interne de l'application, fixé par le serveur. */
  | { opening: "link"; href: string };

export const aiApi = {
  initiatives: async () => {
    const { data } = await http.get<{ items?: AiInitiative[] }>(
      "/v1/ai/initiatives",
    );
    return Array.isArray(data?.items) ? data.items : [];
  },
  openInitiative: async (id: string, requestId: string, choice?: number) =>
    (
      await http.post<AiInitiativeOpening>(`/v1/ai/initiatives/${id}/open`, {
        requestId,
        ...(choice === undefined ? {} : { choice }),
      })
    ).data,
  initiativeUsage: async (days: number) => {
    const { data } = await http.get<AiInitiativeUsageReport>(
      "/v1/ai/initiatives/usage",
      { params: { days } },
    );
    return {
      days: Number(data?.days) || days,
      minDecisions: Number(data?.minDecisions) || 10,
      kinds: Array.isArray(data?.kinds) ? data.kinds : [],
    };
  },
  studentReplies: async () =>
    studentRepliesOf(
      (await http.get<AiStudentReplies>("/v1/ai/student-replies")).data,
    ),
  setStudentReplies: async (enabled: boolean) =>
    studentRepliesOf(
      (await http.put<AiStudentReplies>("/v1/ai/student-replies", { enabled }))
        .data,
    ),
  delegations: async () =>
    delegationsOf((await http.get<AiDelegations>("/v1/ai/delegations")).data),
  setDelegation: async ({
    kind,
    enabled,
    maxLevel,
  }: {
    kind: string;
    enabled: boolean;
    maxLevel?: number;
  }) =>
    delegationsOf(
      (
        await http.put<AiDelegations>(
          `/v1/ai/delegations/${encodeURIComponent(kind)}`,
          { enabled, ...(maxLevel === undefined ? {} : { maxLevel }) },
        )
      ).data,
    ),
  studentDraft: async (conversationId: string, requestId: string) =>
    (
      await http.post<AiStudentDraft>(
        "/v1/ai/student-drafts",
        { conversationId, requestId },
        { timeout: 90000 },
      )
    ).data,
  /** Fait le report préparé avec le brouillon, au nom de la personne ; rend le brouillon à jour. */
  moveStudentLesson: async (id: string, requestId: string) =>
    (
      await http.post<AiStudentDraft>(`/v1/ai/student-drafts/${id}/move`, {
        requestId,
        acknowledgePlanningChecks: true,
      })
    ).data,
  studentDraftUsed: async (id: string) =>
    (await http.post(`/v1/ai/student-drafts/${id}/used`, {})).data,
  /** « Reformuler » (J47) : une autre version du message, proposée avant l'envoi ; rien n'est gardé. */
  rephrase: async (message: string, requestId: string) =>
    (
      await http.post<{ text: string; unchanged: boolean }>(
        "/v1/ai/rephrase",
        { message, requestId },
        { timeout: 90000 },
      )
    ).data,
  dismissInitiative: async (id: string) =>
    (await http.post(`/v1/ai/initiatives/${id}/dismiss`, {})).data,
  restoreConversation: async (id: string) =>
    (
      await http.get<{
        id: string;
        expiresAt: string;
        turns: {
          question: string;
          response: AiAnswer;
          /** Cartes de l'échange, par identifiant : replacées sous sa réponse. */
          cards?: { proposals: string[]; plans: string[] };
        }[];
        proposals: AiProposal[];
        plans: AiPlan[];
        unfinished: {
          requestId: string;
          message: string;
          references: WorkspaceReference[];
          running: boolean;
        } | null;
      } | null>(`/v1/ai/conversations/${id}`)
    ).data,
  knowledgeSources: async (page = 1) =>
    (
      await http.get<{ items: KnowledgeSource[]; hasMore: boolean }>(
        "/v1/ai/knowledge/sources",
        { params: { page } },
      )
    ).data,
  publishKnowledge: async (
    id: string,
    body: { requestId: string; version: number; enabled: boolean },
  ) => (await http.post(`/v1/ai/knowledge/notes/${id}`, body)).data,
  resolveCitations: async (ids: string[]) =>
    (
      await http.post<{
        citations: KnowledgeCitation[];
        allAvailable: boolean;
        observedAt: string;
      }>("/v1/ai/knowledge/citations/resolve", { ids })
    ).data,
  streamMessage,
  plans: async () => (await http.get<AiPlan[]>("/v1/ai/plans")).data,
  preparePlan: async (id: string) =>
    (
      await http.post<AiPlan>(
        `/v1/ai/plans/${id}/prepare`,
        {},
        { timeout: 160000 },
      )
    ).data,
  decidePlan: async (id: string, body: AiPlanDecision) =>
    (
      await http.post<AiPlan>(`/v1/ai/plans/${id}/decision`, body, {
        timeout: 160000,
      })
    ).data,
  usage: async () =>
    (
      await http.get<{
        budgetMicroUsd: number | null;
        remainingMicroUsd: number | null;
        reservedMicroUsd: number;
        calls: number;
        day: string;
      }>("/v1/ai/usage")
    ).data,
  status: async () =>
    (
      await http.get<{
        available: boolean;
        canPropose: boolean;
        capabilities: string[];
        streaming?: boolean;
        conversations?: boolean;
        voice?: boolean;
      }>("/v1/ai/status")
    ).data,
  message: async (body: AiMessage, signal?: AbortSignal) =>
    (
      await http.post<AiAnswer>("/v1/ai/messages", body, {
        timeout: 160000,
        signal,
      })
    ).data,
  proposals: async () =>
    (await http.get<AiProposal[]>("/v1/ai/proposals")).data,
  decide: async (
    id: string,
    decision: "confirm" | "cancel",
    acknowledgePlanningChecks?: boolean,
    acknowledgeOperationReview?: boolean,
  ) =>
    (
      await http.post<AiProposal>(`/v1/ai/proposals/${id}/decision`, {
        decision,
        ...(acknowledgePlanningChecks === undefined
          ? {}
          : { acknowledgePlanningChecks }),
        ...(acknowledgeOperationReview === undefined
          ? {}
          : { acknowledgeOperationReview }),
      })
    ).data,
  lessonCard: async (card: AiLessonCard) =>
    (await http.post<AiProposal>("/v1/ai/proposals/lesson", card)).data,
  document: async (id: string, kind: "original" | "signed" | "certificate") =>
    (
      await http.get<Blob>(`/v1/ai/proposals/${id}/documents/${kind}`, {
        responseType: "blob",
      })
    ).data,
};
